import React, {
	useEffect,
	useState,
	useContext,
	useRef,
	useMemo,
	useCallback
} from 'react';
import { createPortal } from 'react-dom';
import { setClassNames } from '@ddc/classnames-js';
import { useLabels } from 'wsm-common-data';
import { g } from '../global-constants';
import useFocusTrap from '../hooks/useFocusTrap';
import { SearchContext, UIContext, ResultsContext } from '../contexts';
import FlyOutContent from './FlyOutContent';

const CLOSE_CLASS_NAMES = [
	'btn-unstyled text-link-muted',
	'py-0',
	'px-0',
	'my-3',
	'border-neutral-0-alpha-3',
	'float-right'
];

const FlyOut = () => {
	const flyOutRef = useRef(null);

	const { searchTerm } = useContext(SearchContext);

	const {
		openButtonRef,
		closeModal,
		inputElementRef,
		modalIsOpen,
		isMobile,
		screenWidth,
		setIsEmbeddedOverrideActive
	} = useContext(UIContext);
	const labels = useLabels();

	const { recentSearches } = useContext(ResultsContext);

	const [localmodalIsOpen, setLocalmodalIsOpen] = useState(false);

	useFocusTrap(
		[openButtonRef, flyOutRef],
		null,
		flyOutRef,
		null,
		openButtonRef,
		modalIsOpen,
		closeModal
	);

	const isWideScreen = screenWidth > 768;

	const classNames = useMemo(
		() => [
			'sts-search-flyout',
			'box',
			'pt-4 pb-4 pl-5 pr-5',
			localmodalIsOpen && 'slide-left',
			!isMobile && !isWideScreen ? 'one-column' : isMobile && 'mobile',
			isWideScreen && 'two-columns'
		],
		[localmodalIsOpen, isMobile, isWideScreen]
	);

	const onClose = useCallback(() => {
		setIsEmbeddedOverrideActive(false);
		closeModal();
	}, [setIsEmbeddedOverrideActive, closeModal]);

	const preventEventPropagation = useCallback((e) => {
		e.stopPropagation();
	}, []);

	const ariaLabelledBy = useMemo(() => {
		if (searchTerm) {
			return 'search-modal-headline';
		} else if (recentSearches?.length > 0) {
			return 'recent-searches-heading';
		}
		return 'default-flyout-label';
	}, [searchTerm, recentSearches]);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setLocalmodalIsOpen(true);
		}, 50);

		if (modalIsOpen) {
			document.body.style.overflow = 'hidden';
			inputElementRef.current?.focus();
		}

		return () => {
			clearTimeout(timeoutId);
		};
	}, [modalIsOpen, inputElementRef]);

	const closeButton = (
		<button
			type="button"
			tabIndex="0"
			aria-label={labels.get('CLOSE_MODAL')}
			className={setClassNames(CLOSE_CLASS_NAMES)}
			data-testid="flyout-close-button"
			onClick={(e) => {
				preventEventPropagation(e);
				onClose(g.CLOSE_BUTTON);
			}}
			onMouseDown={preventEventPropagation}
			onTouchStart={preventEventPropagation}
			onKeyDown={(e) => {
				if (e.key === 'Enter') {
					preventEventPropagation(e);
					onClose(g.CLOSE_BUTTON);
					setTimeout(() => {
						openButtonRef.current?.focus(); // Focus on the button after a delay
					}, 10);
				}
			}}
		>
			<i
				className={setClassNames([
					'ddc-icon',
					'ddc-icon-remove2',
					'icon-size-2'
				])}
				aria-hidden="true"
			/>
		</button>
	);

	const flyOutContent = (
		<>
			{/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
			<div
				className={setClassNames(['sts-search-modal', 'clearfix'])}
				role="none"
				data-testid="sts-flyout-background"
				onClick={() => onClose(g.CLOSE_BUTTON)}
				data-widget-name="ws-site-text-search"
			/>
			<div
				className={setClassNames(classNames)}
				role="dialog"
				aria-labelledby={ariaLabelledBy}
				ref={flyOutRef}
				tabIndex="-1"
				data-testid="sts-flyout"
				data-widget-name="ws-site-text-search"
			>
				<FlyOutContent closeButton={closeButton} />
			</div>
		</>
	);

	return createPortal(flyOutContent, document.body);
};

export default React.memo(FlyOut);
